import { type Message as AgUiMessage, type ToolCall } from '@ag-ui/client'
import { alpha } from '@precis-digital/kurama/utils'
import { type Theme } from '@precis-digital/kurama/theme'

export const COPILOT_ELEMENT_HIGHLIGHT_DURATION_MS = 4500
export const COPILOT_ELEMENT_HIGHLIGHT_LOOP_DURATION_MS = 1500

export const COPILOT_ACTION_HIGHLIGHT_ATTRIBUTE = 'data-copilot-action-highlight'
export const COPILOT_FOCUS_HIGHLIGHT_ATTRIBUTE = 'data-copilot-focus-highlight'
export const COPILOT_FRONTEND_TOOL_CALL_ID_ATTRIBUTE = 'data-copilot-frontend-tool-call-id'

export const COPILOT_FOCUS_RING_WIDTH_PX = 4
export const COPILOT_ACTION_RING_WIDTH_PX = 2

export const COPILOT_FOCUS_HIGHLIGHT_STYLE_ID = 'copilot-focus-highlight-styles'
export const COPILOT_ACTION_HIGHLIGHT_STYLE_ID = 'copilot-action-highlight-styles'

/** Keyframe stops for the action-ring color sweep (higher = smoother). */
export const COPILOT_ACTION_HIGHLIGHT_KEYFRAME_STOPS = 18

export type CopilotHighlightRingKind = 'focus' | 'action'

export interface CopilotElementHighlightEffect {
  cleanup: () => void
}

export interface CopilotFrontendToolInvocation {
  toolCallId: string
  toolName: string
}

/** Figma: Gradient/AI/subtle */
const getCopilotAiSubtleGradientColors = (
  theme: Theme
): {
  teal: string
  blue: string
  pink: string
  violet: string
} => ({
  teal: theme.tokens.dataViz.primary[2].border.subtle,
  blue: theme.tokens.background.brand.main.default,
  pink: theme.tokens.dataViz.secondary[1].border.subtle,
  violet: theme.tokens.dataViz.primary[5].fill.default
})

/** Figma: Gradient/AI/strong */
const COPILOT_AI_STRONG_GRADIENT_OPACITY = 0.8

/** Starting angle for the action-ring conic gradient (`from` in CSS). */
export const COPILOT_ACTION_GRADIENT_ROTATION_START_DEG = -90

/** One half color sweep around the action ring (degrees). */
export const COPILOT_ACTION_GRADIENT_ROTATION_TOTAL_DEG = 90

/** Duration of the traveling gradient on AI-triggered action highlights. */
export const COPILOT_ACTION_HIGHLIGHT_ANIMATION_DURATION_MS = 4500

/** Figma: Drop shadow — X/Y 0, blur 15, spread 0, #99AEF9 @ 60% */
export const COPILOT_AI_FOCUS_SHADOW_COLOR = '#99AEF9'
export const COPILOT_AI_FOCUS_SHADOW_BLUR_PX = 15

export const COPILOT_FOCUS_PULSE_ITERATIONS =
  COPILOT_ELEMENT_HIGHLIGHT_DURATION_MS / COPILOT_ELEMENT_HIGHLIGHT_LOOP_DURATION_MS

/**
 * Escapes a string for safe use inside a CSS attribute selector value.
 * Prefixes backslashes and double quotes so selectors remain valid.
 */
export const escapeCssAttributeValue = (value: string): string => value.replace(/["\\]/g, String.raw`\$&`)

const buildCopilotAiSubtleGradientStops = (
  theme: Theme
): { teal: string; blue: string; violet: string; pink: string } => {
  const { teal, blue, violet, pink } = getCopilotAiSubtleGradientColors(theme)

  return {
    teal: alpha(teal, 0.16),
    blue: alpha(blue, 0.16),
    violet: alpha(violet, 0.18),
    pink: alpha(pink, 0.21)
  }
}

/** Figma: Gradient/AI/subtle — horizontal fill for UI surfaces such as suggested prompts. */
export const buildCopilotAiSubtleLinearGradient = (theme: Theme): string => {
  const { teal, blue, pink } = buildCopilotAiSubtleGradientStops(theme)

  return `linear-gradient(90deg, ${teal} 0%, ${blue} 50%, ${pink} 100%)`
}

/**
 * Soft mesh halo behind the empty-state composer (Gemini-style ambient glow),
 * using the same AI/subtle teal → blue → pink → violet palette as suggested prompts.
 * Light mode uses lower opacity so the mesh stays subtle on pale backgrounds.
 */
export const buildCopilotAiSubtleAmbientGlow = (theme: Theme): string => {
  const { teal, blue, pink, violet } = getCopilotAiSubtleGradientColors(theme)
  const isLight = theme.mode === 'light'
  const centerAlpha = isLight ? 0.14 : 0.28
  const midAlpha = isLight ? 0.1 : 0.2
  const edgeAlpha = isLight ? 0.07 : 0.14

  return [
    `radial-gradient(ellipse 55% 70% at 30% 40%, ${alpha(blue, centerAlpha)} 0%, transparent 70%)`,
    `radial-gradient(ellipse 50% 75% at 72% 35%, ${alpha(pink, midAlpha)} 0%, transparent 68%)`,
    `radial-gradient(ellipse 60% 80% at 48% 70%, ${alpha(teal, midAlpha)} 0%, transparent 72%)`,
    `radial-gradient(ellipse 45% 65% at 62% 58%, ${alpha(violet, edgeAlpha)} 0%, transparent 65%)`,
    `radial-gradient(ellipse 70% 90% at 50% 50%, ${alpha(blue, edgeAlpha)} 0%, transparent 75%)`
  ].join(', ')
}

/**
 * Builds the Figma AI/subtle conic gradient used for focus highlight rings.
 * Combines teal, blue, violet, and pink stops around a centered conic sweep.
 */
export const buildCopilotAiSubtleConicGradient = (theme: Theme): string => {
  const { teal, blue, violet, pink } = buildCopilotAiSubtleGradientStops(theme)

  return `conic-gradient(
    from -90deg at 50% 50%,
    ${blue} 0deg,
    ${violet} 45deg,
    ${pink} 90deg,
    ${pink} 135deg,
    ${blue} 180deg,
    ${teal} 270deg,
    ${teal} 360deg
  )`
}

/**
 * Builds the Figma AI/strong conic gradient at a given rotation for action highlight rings.
 * Used by the animated action-ring color sweep keyframes.
 */
export const buildCopilotAiStrongConicGradient = (theme: Theme, gradientRotation: string): string => {
  const { teal, blue, pink } = getCopilotAiSubtleGradientColors(theme)

  const strongTeal = alpha(teal, COPILOT_AI_STRONG_GRADIENT_OPACITY)
  const strongBlue = alpha(blue, COPILOT_AI_STRONG_GRADIENT_OPACITY)
  const strongPink = alpha(pink, COPILOT_AI_STRONG_GRADIENT_OPACITY)

  return `conic-gradient(
    from ${gradientRotation},
    ${strongTeal} 0deg,
    ${strongBlue} 120deg,
    ${strongPink} 240deg,
    ${strongTeal} 360deg
  )`
}

const parseBorderWidthPx = (value: string): number => {
  const parsed = Number.parseFloat(value)

  return Number.isFinite(parsed) ? parsed : 0
}

/**
 * Returns the largest computed border width on an element.
 * Used to offset highlight rings so they sit outside existing borders.
 */
export const getElementMaxBorderWidthPx = (element: HTMLElement): number => {
  const { borderTopWidth, borderRightWidth, borderBottomWidth, borderLeftWidth } = getComputedStyle(element)

  return Math.max(
    parseBorderWidthPx(borderTopWidth),
    parseBorderWidthPx(borderRightWidth),
    parseBorderWidthPx(borderBottomWidth),
    parseBorderWidthPx(borderLeftWidth)
  )
}

/** Outset from padding edge to the ring's outer edge (existing border + ring width). */
export const getCopilotHighlightRingOutsetPx = (element: HTMLElement, ringWidthPx: number): number =>
  getElementMaxBorderWidthPx(element) + ringWidthPx

/**
 * CSS selector for the DOM node tagged with a frontend tool call id.
 * Matches elements annotated by `addCopilotFrontendToolCallIdToElement`.
 */
export const getCopilotFrontendToolElementSelector = (toolCallId: string): string =>
  `[${COPILOT_FRONTEND_TOOL_CALL_ID_ATTRIBUTE}="${escapeCssAttributeValue(toolCallId)}"]`

/**
 * Collects completed frontend tool invocations from AG-UI assistant and tool messages.
 * A tool is completed when a matching tool-result message exists for its call id.
 */
export const getCompletedFrontendToolInvocations = (messages: AgUiMessage[]): CopilotFrontendToolInvocation[] => {
  const completedToolCallIds = new Set(
    messages
      .filter((message): message is AgUiMessage & { role: 'tool'; toolCallId: string } => message.role === 'tool')
      .map((message) => message.toolCallId)
  )

  return messages.flatMap((message) => {
    if (message.role !== 'assistant') return []

    return (message.toolCalls ?? [])
      .filter((toolCall: ToolCall) => completedToolCallIds.has(toolCall.id))
      .map((toolCall: ToolCall) => ({
        toolCallId: toolCall.id,
        toolName: toolCall.function.name
      }))
  })
}

/**
 * Tags a DOM element with the tool call id used for scroll-into-view and highlight targeting.
 * Writes the `data-copilot-frontend-tool-call-id` attribute.
 */
export const addCopilotFrontendToolCallIdToElement = (element: HTMLElement, toolCallId: string): void => {
  element.setAttribute(COPILOT_FRONTEND_TOOL_CALL_ID_ATTRIBUTE, toolCallId)
}

export interface CopilotHighlightLayoutState {
  savedPosition: string
  savedOverflow: string
}

/**
 * Prepares an element's layout and CSS variables so a highlight ring renders correctly.
 * Ensures non-static positioning, visible overflow, and ring offset variables.
 */
export const applyCopilotHighlightLayout = (
  element: HTMLElement,
  ringVariablePrefix: CopilotHighlightRingKind,
  ringWidthPx: number
): CopilotHighlightLayoutState => {
  const savedPosition = element.style.position
  const savedOverflow = element.style.overflow

  if (getComputedStyle(element).position === 'static') {
    element.style.position = 'relative'
  }

  const computedOverflow = getComputedStyle(element).overflow
  if (computedOverflow === 'hidden' || computedOverflow === 'clip') {
    element.style.overflow = 'visible'
  }

  const computedStyle = getComputedStyle(element)
  const ringOutsetPx = getCopilotHighlightRingOutsetPx(element, ringWidthPx)

  element.style.setProperty(`--copilot-${ringVariablePrefix}-ring-width`, `${ringWidthPx}px`)
  element.style.setProperty(`--copilot-${ringVariablePrefix}-ring-outset`, `${ringOutsetPx}px`)

  if (computedStyle.borderRadius !== '' && computedStyle.borderRadius !== '0px') {
    element.style.setProperty(`--copilot-${ringVariablePrefix}-border-radius`, computedStyle.borderRadius)
  }

  return { savedPosition, savedOverflow }
}

/**
 * Restores an element's layout and removes highlight ring CSS variables after cleanup.
 * Reverts changes made by `applyCopilotHighlightLayout`.
 */
export const clearCopilotHighlightLayout = (
  element: HTMLElement,
  ringVariablePrefix: CopilotHighlightRingKind,
  { savedPosition, savedOverflow }: CopilotHighlightLayoutState
): void => {
  element.style.position = savedPosition
  element.style.overflow = savedOverflow
  element.style.removeProperty(`--copilot-${ringVariablePrefix}-ring-width`)
  element.style.removeProperty(`--copilot-${ringVariablePrefix}-ring-outset`)
  element.style.removeProperty(`--copilot-${ringVariablePrefix}-border-radius`)
}

/**
 * Schedules automatic highlight cleanup after a duration and tracks it in `activeCleanups`.
 * Returns a handle that clears the timeout and runs cleanup immediately when invoked.
 */
export const scheduleCopilotElementHighlightCleanup = (
  cleanup: () => void,
  activeCleanups: Set<() => void>,
  durationMs: number = COPILOT_ELEMENT_HIGHLIGHT_DURATION_MS
): CopilotElementHighlightEffect => {
  let hasCleanedUp = false

  function wrappedCleanup(): void {
    if (hasCleanedUp) return

    hasCleanedUp = true
    globalThis.clearTimeout(timeoutId)
    cleanup()
    activeCleanups.delete(wrappedCleanup)
  }

  const timeoutId = globalThis.setTimeout(wrappedCleanup, durationMs)
  activeCleanups.add(wrappedCleanup)

  return { cleanup: wrappedCleanup }
}
